import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import isValidCompoundMetricExpression from 'screens/DataManager/Metrics/isValidCompoundMetricExpression';

import CompoundMetricForm from './CompoundMetricForm';
import CurrentUserContext from '../../../../../../contexts/CurrentUserContext';
import MetricCacheContext from '../../../../../../contexts/MetricCacheContext';
import MetricOptionsContext from '../../../../../../contexts/MetricOptionsContext';
import AnalyticsContext from '../../../../../../contexts/AnalyticsContext';
import getTimeStamp from '../../../../../../getTimeStamp';
import ReactPortal from '../../../../../../components/ReactPortal';
import { MetricPopupPortalIdsContext } from '../../MetricPopupPortalIdsContext';
import Preview from '../../Preview';
import BonusMetricAlert from '../BonusMetricAlert';
import MetricActions from '../../MetricActions';
import withoutNulls from '../../../../../../api/search/withoutNulls';
import CoreToggle from '../CoreToggle';
import useEditingState from '../../MetricActions/useEditingState';
import isDefined from '../../../../../../isDefined';
import metricTypeCheckers from '../../../../../../types/metricTypeCheckers';
import CompoundMetricDuplicationWarning from './CompoundMetricDuplicationWarning';
import {
  getExpressionError,
  toIdExpression,
  toLetterExpression,
} from './expressionAliases';

const useUsedMetrics = (metricIds: string[]) => {
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const [usedMetrics, setUsedMetrics] = useState<Metrics.NormalMetric[]>([]);

  useEffect(() => {
    setUsedMetrics(
      metricIds
        .map((mid) => metricOptionsLookup[mid])
        .filter(isDefined)
        .filter(metricTypeCheckers.isNormalMetric),
    );
  }, [metricIds, metricOptionsLookup]);

  return usedMetrics;
};

const CompoundMetricFormContainer = ({
  id,
  selectedMetric,
  showFlash,
  setHasUnsavedChanges,
}: {
  id: string;
  selectedMetric?: Metrics.CompoundMetric;
  showFlash: (message: string) => void;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { previewDivId, primaryActionDivId, coreToggleDivId } = useContext(
    MetricPopupPortalIdsContext,
  );
  const currentUser = useContext(CurrentUserContext);
  const { requestMetricsByIds } = useContext(MetricCacheContext);
  const { trackEvent } = useContext(AnalyticsContext);

  // When editing a compound metric, its input metricIds may include single-use
  // metrics that aren't loaded by requestAllNormalMetrics (which filters by
  // status). Explicitly request them by ID so they enter the cache.
  useEffect(() => {
    if (selectedMetric && metricTypeCheckers.isCompoundMetric(selectedMetric)) {
      requestMetricsByIds(selectedMetric.metricIds);
    }
  }, [selectedMetric, requestMetricsByIds]);
  const [name, setName] = useState<string>(
    selectedMetric ? selectedMetric.name : '',
  );
  const [description, setDescription] = useState<string>(
    selectedMetric && selectedMetric.description
      ? selectedMetric.description
      : '',
  );
  const [status, setStatus] = useState<Metrics.MetricStatus>(
    selectedMetric ? selectedMetric.status : 'public',
  );
  const [selectedMetricIds, setSelectedMetricIds] = useState<string[]>(
    selectedMetric ? selectedMetric.metricIds : [],
  );
  const usedMetrics = useUsedMetrics(selectedMetricIds);
  const [expression, setExpression] = useState(
    selectedMetric ? selectedMetric.expression : '',
  );
  const [formatting, setFormatting] = useState(
    selectedMetric ? selectedMetric.formatting : {},
  );
  const isExpressionValid = useMemo(
    () =>
      isValidCompoundMetricExpression({
        expression,
        metricIds: selectedMetricIds,
      }),
    [expression, selectedMetricIds],
  );

  const isValid = useMemo(
    () => selectedMetricIds.length > 0 && isExpressionValid,
    [isExpressionValid, selectedMetricIds.length],
  );

  // The form shows input metrics as letters (A, B...) rather than raw IDs
  const letterExpression = useMemo(
    () => toLetterExpression({ expression, metricIds: selectedMetricIds }),
    [expression, selectedMetricIds],
  );
  // Names of input metrics removed while editing, to explain the expression
  // error if they are still referenced
  const [removedMetricNames, setRemovedMetricNames] = useState<{
    [metricId: string]: string;
  }>({});
  const expressionError = useMemo(
    () =>
      isExpressionValid
        ? undefined
        : getExpressionError({
            letterExpression,
            metricIds: selectedMetricIds,
            removedMetricNames,
          }),
    [
      isExpressionValid,
      letterExpression,
      removedMetricNames,
      selectedMetricIds,
    ],
  );

  const metricDraft = useMemo((): Metrics.CompoundMetric | undefined => {
    if (!isValid) {
      return undefined;
    }
    return withoutNulls({
      id,
      name,
      description,
      status,
      type: 'compound',
      formatting,
      metricIds: selectedMetricIds,
      expression,
      createdBy: selectedMetric ? selectedMetric.createdBy : currentUser.id,
      createdOn: selectedMetric ? selectedMetric.createdOn : getTimeStamp(),
      updatedBy: currentUser.id,
      updatedOn: getTimeStamp(),
    });
  }, [
    currentUser.id,
    description,
    expression,
    formatting,
    id,
    isValid,
    name,
    selectedMetric,
    selectedMetricIds,
    status,
  ]);

  const editingState = useEditingState({
    selectedMetric,
    metricDraft,
    invalidForm: !isValid,
  });
  useEffect(() => {
    if (selectedMetric) {
      setHasUnsavedChanges(
        editingState !== 'no changes' &&
          editingState !== 'no changes - archived',
      );
      return;
    }

    setHasUnsavedChanges(
      name !== '' ||
        description !== '' ||
        selectedMetricIds.length > 0 ||
        expression !== '',
    );
  }, [
    description,
    editingState,
    expression,
    name,
    selectedMetric,
    selectedMetricIds.length,
    setHasUnsavedChanges,
  ]);

  const onNameChanged = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  }, []);

  const onDescriptionChanged = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setDescription(event.target.value);
    },
    [],
  );

  const onExpressionChanged = useCallback(
    (newLetterExpression: string) => {
      setExpression(
        toIdExpression({
          letterExpression: newLetterExpression,
          metricIds: selectedMetricIds,
        }),
      );
    },
    [selectedMetricIds],
  );

  const onAddMetric = useCallback(
    (id: string) => {
      setSelectedMetricIds([...selectedMetricIds, id]);
      setRemovedMetricNames((current) => {
        const newNames = { ...current };
        delete newNames[id];
        return newNames;
      });
      trackEvent('Compound Metric Builder - Metric added');
    },
    [selectedMetricIds, trackEvent],
  );

  const onRemoveMetric = useCallback(
    (id: string) => {
      const removedMetric = usedMetrics.find((m) => m.id === id);
      if (removedMetric) {
        setRemovedMetricNames((current) => ({
          ...current,
          [id]: removedMetric.name,
        }));
      }
      setSelectedMetricIds(selectedMetricIds.filter((mId) => mId !== id));
      trackEvent('Compound Metric Builder - Metric removed');
    },
    [selectedMetricIds, trackEvent, usedMetrics],
  );

  const onMetricPickerOpened = useCallback(() => {
    trackEvent('Compound Metric Builder - Add metric clicked');
  }, [trackEvent]);

  return (
    <>
      <CompoundMetricForm
        name={name}
        onNameChanged={onNameChanged}
        description={description}
        onDescriptionChanged={onDescriptionChanged}
        formatting={formatting}
        setFormatting={setFormatting}
        selectedMetricIds={selectedMetricIds}
        selectedMetrics={usedMetrics}
        onAddMetric={onAddMetric}
        onRemoveMetric={onRemoveMetric}
        expression={letterExpression}
        onExpressionChanged={onExpressionChanged}
        expressionError={expressionError}
        onMetricPickerOpened={onMetricPickerOpened}
      />
      <ReactPortal elementId={previewDivId}>
        {!!metricDraft && (
          <Preview metricDraft={metricDraft} isValid={isValid} />
        )}
        <BonusMetricAlert />
        {!!metricDraft && (
          <CompoundMetricDuplicationWarning metricDraft={metricDraft} />
        )}
      </ReactPortal>
      <ReactPortal elementId={primaryActionDivId}>
        <MetricActions
          metricDraft={metricDraft}
          selectedMetric={selectedMetric}
          invalidForm={!isValid}
          setStatus={setStatus}
          showFlash={showFlash}
        />
      </ReactPortal>
      <ReactPortal elementId={coreToggleDivId}>
        <CoreToggle
          status={status}
          setStatus={setStatus}
          selectedMetric={selectedMetric}
        />
      </ReactPortal>
    </>
  );
};

export default CompoundMetricFormContainer;
